/**
 * Leitura do `diagnostico` do `simulate` (L13).
 *
 * ## Por que isto existe
 *
 * `permitido: true` **só é conclusivo quando o `diagnostico` não reporta
 * verificação faltando**. Enquanto a AIG-08 não existir, o middleware pode
 * responder `permitido: true` com um `diagnostico` que diz "limite não
 * verificado" — e aí o ALLOW significa "permitido **ignorando os limites**", não
 * "seria permitido". Um ALLOW com asterisco é honesto; um ALLOW limpo sobre
 * limites não avaliados não é.
 *
 * ## Por que é heurística, e o que isso obriga
 *
 * O qualificador vive num objeto de **forma livre**: o contrato não nomeia chave
 * nem valor, só diz que o aviso pode estar ali. Então esta função procura
 * marcadores no texto — e pode não achar um qualificador escrito de um jeito que
 * ninguém previu.
 *
 * Daí a segunda metade da regra, que está na tela: o `diagnostico` **inteiro** é
 * renderizado ao lado do veredito, sempre, mesmo quando nada casa aqui. A
 * heurística grita quando reconhece; o objeto cru é o que garante que um
 * qualificador desconhecido ainda chegue aos olhos de quem decide.
 */

const MARKERS: RegExp[] = [
  /n[ãa]o[\s_-]*verificad/i,
  /n[ãa]o[\s_-]*avaliad/i,
  /n[ãa]o[\s_-]*checad/i,
  /sem[\s_-]*verifica/i,
  /not[\s_-]*verified/i,
  /unverified/i,
  /unchecked/i,
  /faltando/i,
  /missing/i,
  /pendente[\s_-]*de[\s_-]*verifica/i,
];

function casaMarker(texto: string): boolean {
  return MARKERS.some((marcador) => marcador.test(texto));
}

export interface DiagnosticRead {
  /** `true` = o diagnóstico reporta alguma verificação que não aconteceu. */
  verificacaoFaltando: boolean;
  /** Trechos reconhecidos, como `caminho: valor`, para exibir junto do veredito. */
  trechos: string[];
  /** `true` quando há qualquer conteúdo a mostrar. */
  temConteudo: boolean;
}

/**
 * Varre o objeto (chaves e valores, recursivamente) atrás dos marcadores.
 *
 * Uma chave que casa conta mesmo com valor `false`/vazio: `{"limite_verificado":
 * false}` e `{"limite_nao_verificado": true}` dizem a mesma coisa, e só a
 * segunda casaria por valor. Por isso a chave é lida junto do valor.
 */
export function readDiagnostic(
  diagnostico: Record<string, unknown> | null | undefined,
): DiagnosticRead {
  const trechos: string[] = [];

  const visitar = (valor: unknown, path: string) => {
    if (valor === null || valor === undefined) return;

    if (Array.isArray(valor)) {
      valor.forEach((item, i) => visitar(item, `${path}[${i}]`));
      return;
    }

    if (typeof valor === 'object') {
      for (const [chave, filho] of Object.entries(valor as Record<string, unknown>)) {
        visitar(filho, path ? `${path}.${chave}` : chave);
      }
      return;
    }

    const texto = String(valor);
    // A chave entra na comparação: "limite_verificado: false" só se reconhece
    // pelo nome do campo, e é exatamente o caso da AIG-08.
    const negativeByKey =
      casaMarker(path) ||
      (texto === 'false' && /verificad|checad|avaliad|verified|checked/i.test(path));
    if (negativeByKey || casaMarker(texto)) {
      trechos.push(`${path}: ${texto}`);
    }
  };

  visitar(diagnostico, '');

  return {
    verificacaoFaltando: trechos.length > 0,
    trechos,
    temConteudo: Boolean(diagnostico && Object.keys(diagnostico).length > 0),
  };
}
